import { Text } from "@imminently/react/ui/core/text";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { ListItem, ListItemText, Stack } from "@mui/material";
import { List } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import { GridColDef } from "@mui/x-data-grid";
import { startCase } from "lodash";
import React, { ReactElement } from "react";
import { useTheme } from "@emotion/react";

import PatientMedicalHistory from "@/components/Patient/PatientMedicalHistory";
import PatientHistoryDataGrid from "@/components/Patient/PatientHistoryDataGrid";
import { PresentingProblems } from "@/components/Patient/PatientPresentingProblems";
import { PatientReporterInform } from "@/components/Patient/PatientReporterInform";
import { SYMPTOM_LABELS } from "@/resources/constants";
import { PatientEncounter } from "@/resources/types";
import { patientService } from "@/services";
import { css, Theme } from "@/theme";
import { AHC_DIRECTIVE, ALCOHOL_STATUS, DRUG_STATUS, EMPLOYMENT_STATUS, HOME_STATUS, HOME_SUPPORT, SMOKE_STATUS } from "./PatientInfoTabConsts";
import { Chip } from "../ui/chip";
import { useTypes } from "@/providers/TypesProvider";

const styles = {
  whiteBox: {
    display: "flex",
    padding: "16px",
    flexWrap: "wrap",
    gap: "8px",
    justifyContent: "flex-start",
    borderRadius: "8px",
    border: "1px solid #E2E2E2",
    background: "white",
  } as React.CSSProperties,
  chip: (theme: Theme): React.CSSProperties => ({
    marginTop: "8px",
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    borderRadius: "4px",
    fontWeight: 500,
  }),
  title: (theme: Theme) => css`
    color: #444;
    font-family: ${theme.typography.h5.fontFamily};
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
  `,
  text1: (theme: Theme) => css`
    .MuiTypography-root {
      color: #161b32;
      font-family: ${theme.typography.body1.fontFamily};
      font-size: 14px;
      font-weight: 400;
      line-height: 130%;
    }
  `,
};

const WhiteBox = ({
  children,
  flexDirection = "column",
}: {
  children?: ReactElement | ReactElement[] | null;
  flexDirection?: "row" | "column";
}) => (
  <Box style={{ ...styles.whiteBox, flexDirection }}>{children || null}</Box>
);

const ChipsBlock = ({ texts }: { texts: string[] }) => {
  const theme = useTheme() as Theme;
  return (
    <WhiteBox flexDirection="row">
      {texts.map((text, index) => (
        <Chip label={text} key={text + "-" + index} style={styles.chip(theme)} />
      ))}
    </WhiteBox>
  );
};

const ListBlock = ({ items }: { items: string[] }) => (
  <WhiteBox>
    <List style={{ padding: "0px" }}>
      {items.map((text, index) => (
        <ListItem style={{ padding: "0px" }} key={`${index}-${text}`}>
          <FiberManualRecordIcon
            style={{ width: "8px", height: "8px", marginRight: "10px" }}
          />
          <ListItemText primary={text} css={styles.text1} />
        </ListItem>
      ))}
    </List>
  </WhiteBox>
);

const TableBlock = ({ rows }: { rows: Array<{ label: string; value: string }> }) => (
  <WhiteBox>
    <Box
      component="table"
      sx={{
        width: "100%",
        borderCollapse: "collapse",
        "& td": {
          padding: "8px 12px",
          borderBottom: "1px solid #E2E2E2",
          fontSize: "14px",
          lineHeight: "130%",
        },
        "& td:first-of-type": {
          fontWeight: 600,
          color: "#444",
          width: "1%",
          whiteSpace: "nowrap",
        },
        "& td:last-of-type": {
          color: "#161b32",
          width: "99%",
        },
        "& tr:last-child td": {
          borderBottom: "none",
        },
      }}
    >
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${index}-${row.label}`}>
            <td>{row.label}</td>
            <td>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </Box>
  </WhiteBox>
);

const TitleAndInformGroup = ({
  children,
  title,
}: {
  children?: ReactElement;
  title: string;
}) => (
  <Box marginTop="24px" width="full">
    <Text marginBottom="16px" css={styles.title}>
      {title}
    </Text>
    {children || null}
  </Box>
);

type When = "lt1month" | "lt6months" | "gt6months";
const getFormattedWhen = (type: When) => {
  switch (type) {
    case "lt1month":
      return "Less than 1 month ago";
    case "lt6months":
      return "1 - 6 months ago";
    case "gt6months":
      return "More than 6 months ago";
  }
};

type Falls = {
  fallenInLastYear?: boolean;
  feelUnsteady?: boolean;
  worriedAboutFalling?: boolean;
};

const getFallsDetails = (falls: Falls) =>
  [
    falls.fallenInLastYear && "Fallen in Last Year",
    falls.feelUnsteady && "Unsteady",
    falls.worriedAboutFalling && "Worried about falling",
  ].filter(Boolean) as string[];

const makeSocialHistoryDetails = (encounter: PatientEncounter) =>
  [
    encounter.employmentStatus &&
    `Employment: ${EMPLOYMENT_STATUS[encounter.employmentStatus]}`,
    encounter.homeStatus &&
    `Living Situation: ${HOME_STATUS[encounter.homeStatus]}`,
    encounter.homeSupport &&
    `Home Support: ${HOME_SUPPORT[encounter.homeSupport]}`,
    encounter.healthcareDirective &&
    `Health Care Directive: ${AHC_DIRECTIVE[encounter.healthcareDirective]}`,
    encounter.alcoholConsumption &&
    `Alcohol Consumption: ${ALCOHOL_STATUS[encounter.alcoholConsumption]}`,
    encounter.tobaccoConsumption &&
    `Smoke Status: ${SMOKE_STATUS[encounter.tobaccoConsumption]}`,
    encounter.recreationalDrugs &&
    `Drug Status: ${DRUG_STATUS[encounter.recreationalDrugs]}`,
  ].filter((it) => it) as string[];

// eslint-disable-next-line complexity
const makeExpectsDetails = (
  encounter: PatientEncounter,
  triageDirectionOptions: Array<{ value: string; label: string }>,
  patientOriginOptions: Array<{ value: string; label: string }>
) => {
  const details: Array<{ label: string; value: string }> = [];

  if (encounter.admittingSpecialty) {
    details.push({ label: "Admitting Specialty", value: encounter.admittingSpecialty });
  }

  if (encounter.bedSlipSubmitted) {
    details.push({
      label: "Bed Slip Submitted",
      value: encounter.bedSlipSubmitted === "yes" ? "Yes" : "No"
    });
  }

  if (encounter.triageDirection) {
    const triageOption = triageDirectionOptions.find(opt => opt.value === encounter.triageDirection);
    details.push({
      label: "Triage Directions",
      value: triageOption?.label || encounter.triageDirection
    });
  }

  if (encounter.patientOrigin) {
    const originOption = patientOriginOptions.find(opt => opt.value === encounter.patientOrigin);
    details.push({
      label: "Patient Origin",
      value: originOption?.label || encounter.patientOrigin
    });
  }

  if (encounter.referrerDetails?.from) {
    details.push({ label: "Referrer Name", value: encounter.referrerDetails.from });
  }

  if (encounter.referrerDetails?.contactName) {
    details.push({ label: "Referrer Contact", value: encounter.referrerDetails.contactName });
  }

  if (encounter.referrerDetails?.contactNumber) {
    details.push({ label: "Referrer Phone", value: encounter.referrerDetails.contactNumber });
  }

  return details;
};

const makeGpDetails = (patient: any) => {
  const details: Array<{ label: string; value: string }> = [];

  if (patient.gpDetails?.clinicName) {
    details.push({ label: "Clinic", value: patient.gpDetails.clinicName });
  }

  if (patient.gpDetails?.doctorLastName || patient.gpDetails?.doctorFirstName) {
    const doctorName = [patient.gpDetails.doctorFirstName, patient.gpDetails.doctorLastName]
      .filter(Boolean)
      .join(" ");
    details.push({ label: "Doctor", value: doctorName });
  }

  return details;
};

const makeNextOfKinDetails = (patient: any, delegateRelationshipOptions: Array<{ value: string; label: string }>) => {
  const details: Array<{ label: string; value: string }> = [];

  if (patient.nextOfKin?.firstName || patient.nextOfKin?.lastName) {
    const name = [patient.nextOfKin.firstName, patient.nextOfKin.lastName]
      .filter(Boolean)
      .join(" ");
    details.push({ label: "Name", value: name });
  }

  if (patient.nextOfKin?.relationship) {
    const relationshipOption = delegateRelationshipOptions.find(
      opt => opt.value === patient.nextOfKin.relationship
    );
    details.push({
      label: "Relationship",
      value: relationshipOption?.label || patient.nextOfKin.relationship
    });
  }

  return details;
};

const getMedicalHistory = (encounter: PatientEncounter) => {
  const result =
    encounter.healthHistory?.map((histItem, index) => ({
      id: `${index}-healthHistory-${histItem.type}`,
      type: histItem.type,
      details: histItem.details,
    })) || [];

  // Regular medication info is not inside healthHistory for some reason
  if (!encounter.regularMeds) {
    return result;
  }

  return [
    ...result,
    {
      id: `${result.length}-healthHistory-regularMeds`,
      type: "regular-meds",
      details:
        encounter.regularMeds === "moreThan3"
          ? "more-than-3"
          : encounter.regularMeds,
    },
  ];
};

const surgHistColumns: GridColDef[] = [
  {
    field: "type",
    headerName: "Operation",
    flex: 1,
    valueFormatter: (value: any) => value ? startCase(value) : "",
  },
  {
    field: "when",
    headerName: "Details",
    flex: 1,
    valueFormatter: (value: any) => value ? getFormattedWhen(value as When) : "",
  },
];

// eslint-disable-next-line complexity
const PatientInfoTab = ({ encounter }: { encounter: PatientEncounter }) => {
  const { data } = patientService.useGetOne(encounter.patient.id);
  const { triageDirectionOptions, patientOriginOptions, delegateRelationshipOptions } = useTypes();

  if (!data) {
    return (
      <Box display="flex" justifyContent="center">
        <Text variant="h4">Loading...</Text>
      </Box>
    );
  }

  const problemPhotos = encounter.photos?.filter(
    (photo) => photo.type === "problem"
  );

  const surgHistRows =
    encounter.previousOperations?.map((histItem, index) => ({
      id: `${index}-previousOperations-${histItem.type}`,
      type: histItem.type,
      when: histItem.when,
    })) || [];

  const fallsDetails = getFallsDetails(encounter.fallRisk ?? {});
  const medHistRows = getMedicalHistory(encounter);
  const socialDetails = makeSocialHistoryDetails(encounter);
  const expectsDetails = makeExpectsDetails(encounter, triageDirectionOptions, patientOriginOptions);
  const gpDetails = makeGpDetails(data);
  const nextOfKinDetails = makeNextOfKinDetails(data, delegateRelationshipOptions);
  const previousNames = data.previousNames || [];
  const symptoms =
    encounter.infectiousSymptoms?.map((symptom) => SYMPTOM_LABELS[symptom]) ||
    [];

  return (
    <Stack gap={1}>
      <Grid container spacing={4} marginTop="16px">
        <Grid size={{ xs: 12, md: 6 }}>
          <TitleAndInformGroup title="Presenting Problems">
            <PresentingProblems
              encounter={encounter}
              problemPhotos={problemPhotos}
            />
          </TitleAndInformGroup>
        </Grid>

        {!!previousNames.length && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TitleAndInformGroup title="Previous Names">
              <ListBlock
                items={previousNames.map((name) =>
                  [name.firstName, name.lastName].filter(Boolean).join(" ")
                )}
              />
            </TitleAndInformGroup>
          </Grid>
        )}

        {!!nextOfKinDetails.length && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TitleAndInformGroup title="Next of Kin">
              <TableBlock rows={nextOfKinDetails} />
            </TitleAndInformGroup>
          </Grid>
        )}

        {!!gpDetails.length && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TitleAndInformGroup title="GP Details">
              <TableBlock rows={gpDetails} />
            </TitleAndInformGroup>
          </Grid>
        )}

        {!!medHistRows.length && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TitleAndInformGroup title="Previous Medical History (patient reported)">
              <PatientMedicalHistory rows={medHistRows} />
            </TitleAndInformGroup>
          </Grid>
        )}

        {!!surgHistRows.length && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TitleAndInformGroup title="Previous Surgical History (patient reported)">
              <PatientHistoryDataGrid
                rows={surgHistRows}
                columns={surgHistColumns}
              />
            </TitleAndInformGroup>
          </Grid>
        )}

        {!!symptoms.length && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TitleAndInformGroup title="Infection Risk Screening">
              <ChipsBlock texts={symptoms} />
            </TitleAndInformGroup>
          </Grid>
        )}

        {fallsDetails.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TitleAndInformGroup title="Falls Risk Screening">
              <ChipsBlock texts={fallsDetails} />
            </TitleAndInformGroup>
          </Grid>
        )}

        {!!socialDetails.length && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TitleAndInformGroup title="Social History">
              <ListBlock items={socialDetails} />
            </TitleAndInformGroup>
          </Grid>
        )}
      </Grid>

      {!!expectsDetails.length && (
        <Box marginTop="24px">
          <TitleAndInformGroup title="Expects Information">
            <TableBlock rows={expectsDetails} />
          </TitleAndInformGroup>
        </Box>
      )}
    </Stack>
  );
};

export default PatientInfoTab;
